import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Skeleton,
} from '@bloomcode/ui';
import { Download, BarChart2 } from '@bloomcode/icons';
import { useInvoices } from '@/hooks';

/**
 * StatementsPage
 *
 * Groups invoices by month to produce downloadable monthly statements.
 * Uses useListPaymentTenantInvoicesQuery via the useInvoices hook.
 */

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR' }).format(
    amount / 100
  );
}

function getMonthYear(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

interface MonthlyStatement {
  monthYear: string;
  invoiceCount: number;
  totalAmount: number;
  paidAmount: number;
  currency: string;
  invoiceUrl?: string | null;
}

export default function StatementsPage() {
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());
  const { invoices, loading, error } = useInvoices({ limit: 100 });

  // Group invoices by month to build statements
  const statements: MonthlyStatement[] = (() => {
    const map = new Map<string, MonthlyStatement>();

    invoices
      .filter((inv) => {
        if (!inv.created_at) return false;
        return new Date(inv.created_at).getFullYear() === yearFilter;
      })
      .forEach((inv) => {
        const key = getMonthYear(inv.created_at!);
        const existing = map.get(key);
        if (existing) {
          existing.invoiceCount += 1;
          existing.totalAmount += inv.total;
          existing.paidAmount += inv.amount_paid;
        } else {
          map.set(key, {
            monthYear: key,
            invoiceCount: 1,
            totalAmount: inv.total,
            paidAmount: inv.amount_paid,
            currency: inv.currency,
            invoiceUrl: inv.invoice_url,
          });
        }
      });

    return Array.from(map.values()).reverse();
  })();

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <>
      <Helmet>
        <title>Statements | Bloomcode Ledger Portal</title>
        <meta name="description" content="Download your monthly account statements." />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-[Manrope]">Statements</h1>
            <p className="text-muted-foreground mt-1">Monthly account statements</p>
          </div>
          <div className="flex items-center gap-2">
            {yearOptions.map((year) => (
              <Button
                key={year}
                variant={yearFilter === year ? 'default' : 'outline'}
                size="sm"
                onClick={() => setYearFilter(year)}
              >
                {year}
              </Button>
            ))}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Monthly Statements — {yearFilter}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {error ? (
              <div className="p-8 text-center text-destructive">
                Failed to load statements. Please try again.
              </div>
            ) : loading ? (
              <div className="divide-y">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between px-6 py-4">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-8 w-28" />
                  </div>
                ))}
              </div>
            ) : statements.length === 0 ? (
              <div className="p-12 text-center">
                <BarChart2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No statements found for {yearFilter}.</p>
              </div>
            ) : (
              <>
                <div className="hidden md:grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-4 px-6 py-3 bg-muted/50 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  <span>Period</span>
                  <span>Invoices</span>
                  <span>Total Billed</span>
                  <span>Total Paid</span>
                  <span>Download</span>
                </div>
                <div className="divide-y">
                  {statements.map((stmt) => (
                    <div
                      key={stmt.monthYear}
                      className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 md:gap-4 px-6 py-4 items-center"
                    >
                      <span className="font-medium text-sm">{stmt.monthYear}</span>
                      <span className="text-sm text-muted-foreground">
                        {stmt.invoiceCount} invoice{stmt.invoiceCount !== 1 ? 's' : ''}
                      </span>
                      <span className="text-sm font-semibold">
                        {formatCurrency(stmt.totalAmount, stmt.currency)}
                      </span>
                      <span className="text-sm text-success">
                        {formatCurrency(stmt.paidAmount, stmt.currency)}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1"
                        disabled={!stmt.invoiceUrl}
                        onClick={() =>
                          stmt.invoiceUrl &&
                          window.open(stmt.invoiceUrl, '_blank', 'noopener,noreferrer')
                        }
                      >
                        <Download className="h-4 w-4" />
                        <span className="hidden sm:inline">Download</span>
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
